import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Heart,
  Lock,
  Loader2,
  Camera,
  Calendar,
  Clock,
  MapPin,
  Users,
  UserCheck,
  UserX,
  HelpCircle,
  Mail,
  Phone,
  UtensilsCrossed,
  UserPlus,
  Send,
  Check,
  Gift,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Helper: format date in Polish long form ---
function formatWeddingDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

// --- Helper: format time (strip seconds if present) ---
function formatTime(timeStr) {
  if (!timeStr) return '';
  const parts = timeStr.split(':');
  return `${parts[0]}:${parts[1]}`;
}

// --- Decorative divider with heart ---
function HeartDivider() {
  return (
    <div className="flex items-center justify-center gap-4 my-12">
      <div className="h-px bg-border flex-1 max-w-[120px]" />
      <Heart className="w-5 h-5 text-rose-400" />
      <div className="h-px bg-border flex-1 max-w-[120px]" />
    </div>
  );
}

// --- Stat Card ---
function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <Card className="text-center">
        <CardContent className="pt-6 pb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            {value}
          </p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// --- Timeline Item ---
function TimelineItem({ time, event, location, isLast, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay }}
      className="flex gap-4"
    >
      {/* Left: time + dot + line */}
      <div className="flex flex-col items-center w-20 shrink-0">
        <span
          className="text-sm font-semibold text-rose-600 mb-2"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          {formatTime(time)}
        </span>
        <div className="w-3 h-3 rounded-full bg-rose-400 ring-4 ring-rose-100 shrink-0" />
        {!isLast && <div className="w-px flex-1 bg-rose-200 mt-1" />}
      </div>

      {/* Right: content */}
      <div className="pb-8 flex-1">
        <h4 className="font-semibold text-base mb-1" style={{ fontFamily: 'Georgia, serif' }}>
          {event}
        </h4>
        {location && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="w-3.5 h-3.5" />
            {location}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================
// Main component
// ============================================================
export default function PublicWeddingPage() {
  const { weddingId } = useParams();

  // --- data state ---
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- password gate ---
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [coupleName, setCoupleName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // --- rsvp form ---
  const [rsvpForm, setRsvpForm] = useState({
    email: '',
    name: '',
    rsvp: 'confirmed',
    dietary: '',
    phone: '',
    plus_one: false
  });
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [rsvpSuccess, setRsvpSuccess] = useState(false);

  // --- scroll state for the hero parallax effect ---
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- fetch wedding data ---
  const fetchWedding = useCallback(async (pwd = null) => {
    try {
      const params = pwd ? `?password=${encodeURIComponent(pwd)}` : '';
      const response = await axios.get(`${API}/public/wedding/${weddingId}${params}`);
      const data = response.data;

      // Backend signals password is required
      if (data.requires_password) {
        setRequiresPassword(true);
        setCoupleName(data.couple_name || '');
        if (pwd) {
          setPasswordError('Nieprawidlowe haslo. Sprobuj ponownie.');
        }
        setLoading(false);
        return;
      }

      setWedding(data);
      setRequiresPassword(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setRequiresPassword(true);
        setCoupleName(err.response?.data?.couple_name || '');
        if (pwd) {
          setPasswordError('Nieprawidlowe haslo. Sprobuj ponownie.');
        }
      } else if (err.response?.status === 404) {
        setError('Nie znaleziono strony wesela. Sprawdz poprawnosc linku.');
      } else {
        setError('Wystapil blad podczas ladowania strony. Sprobuj ponownie pozniej.');
      }
    } finally {
      setLoading(false);
      setPasswordLoading(false);
    }
  }, [weddingId]);

  useEffect(() => {
    fetchWedding();
  }, [fetchWedding]);

  // --- password submit ---
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setPasswordError('');
    setPasswordLoading(true);
    fetchWedding(password);
  };

  // --- rsvp submit ---
  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    if (!rsvpForm.email || !rsvpForm.name) {
      toast.error('Podaj imie i adres email.');
      return;
    }
    setRsvpSubmitting(true);
    try {
      await axios.post(`${API}/public/wedding/${weddingId}/rsvp`, {
        email: rsvpForm.email,
        name: rsvpForm.name,
        rsvp: rsvpForm.rsvp,
        dietary: rsvpForm.dietary || null,
        phone: rsvpForm.phone || null,
        plus_one: rsvpForm.plus_one
      });
      setRsvpSuccess(true);
      toast.success('Dziekujemy za potwierdzenie!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Wystapil blad. Sprobuj ponownie.');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const updateRsvpField = (field, value) => {
    setRsvpForm((prev) => ({ ...prev, [field]: value }));
  };

  // ============================================================
  // RENDER: Loading
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-rose-400" />
          <p className="text-muted-foreground text-sm">Ladowanie...</p>
        </motion.div>
      </div>
    );
  }

  // ============================================================
  // RENDER: Error
  // ============================================================
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Ups...
          </h1>
          <p className="text-muted-foreground">{error}</p>
        </motion.div>
      </div>
    );
  }

  // ============================================================
  // RENDER: Password Gate
  // ============================================================
  if (requiresPassword && !wedding) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50/50 to-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-rose-500" />
          </div>

          {coupleName && (
            <h1
              className="text-3xl font-bold mb-2 text-foreground"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {coupleName}
            </h1>
          )}
          <p className="text-muted-foreground mb-8">
            Ta strona jest chroniona haslem.
            <br />
            Wpisz haslo otrzymane w zaproszeniu.
          </p>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Haslo z zaproszenia"
              className="h-12 text-center text-lg"
              autoFocus
            />
            {passwordError && (
              <p className="text-destructive text-sm">{passwordError}</p>
            )}
            <Button
              type="submit"
              className="w-full h-12 bg-rose-500 hover:bg-rose-600 text-white"
              disabled={passwordLoading || !password.trim()}
            >
              {passwordLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Heart className="w-4 h-4 mr-2" />
              )}
              Wejdz na strone
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ============================================================
  // RENDER: Main Wedding Page
  // ============================================================
  const {
    couple_name,
    wedding_date,
    wedding_time,
    venue_name,
    venue_address,
    description,
    rsvp_stats,
    photographer_name,
    branding,
    agenda,
    gift_registry_url,
    cover_image_url
  } = wedding || {};

  const stats = rsvp_stats || {};
  const brandColor = branding?.primary_color || '#e11d48'; // rose-600 default
  const coverUrl = cover_image_url || branding?.cover_url;
  const agendaItems = agenda || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/30 to-background">
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        {coverUrl ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${coverUrl})`,
              transform: `translateY(${scrollY * 0.3}px)`
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-pink-50 to-amber-50" />
        )}
        <div className="absolute inset-0 bg-black/40" />

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Heart className="w-10 h-10 text-white/80 mx-auto mb-6" />

            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {couple_name || 'Wesele'}
            </h1>

            {wedding_date && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-xl md:text-2xl text-white/90 mb-2"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {formatWeddingDate(wedding_date)}
              </motion.p>
            )}

            {wedding_time && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-white/70 flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                {formatTime(wedding_time)}
              </motion.p>
            )}

            {venue_name && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-lg text-white/70 mt-2 flex items-center justify-center gap-2"
              >
                <MapPin className="w-4 h-4" />
                {venue_name}
                {venue_address && `, ${venue_address}`}
              </motion.p>
            )}

            {photographer_name && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-6 text-sm text-white/50 flex items-center justify-center gap-1.5"
              >
                <Camera className="w-3.5 h-3.5" />
                Fotograf: {photographer_name}
              </motion.p>
            )}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <ChevronDown className="w-6 h-6 text-white/50 animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* ===== DESCRIPTION ===== */}
      {description && (
        <section className="max-w-3xl mx-auto px-6 py-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg leading-relaxed text-muted-foreground"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {description}
          </motion.p>
        </section>
      )}

      {/* ===== RSVP STATS ===== */}
      {(stats.total > 0 || stats.confirmed > 0 || stats.declined > 0 || stats.pending > 0) && (
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <HeartDivider />
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-center mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Goscie
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Zaproszeni"
              value={stats.total ?? 0}
              color="#6366f1"
              delay={0}
            />
            <StatCard
              icon={UserCheck}
              label="Potwierdzeni"
              value={stats.confirmed ?? 0}
              color="#22c55e"
              delay={0.1}
            />
            <StatCard
              icon={UserX}
              label="Odmowili"
              value={stats.declined ?? 0}
              color="#ef4444"
              delay={0.2}
            />
            <StatCard
              icon={HelpCircle}
              label="Oczekujacy"
              value={stats.pending ?? 0}
              color="#f59e0b"
              delay={0.3}
            />
          </div>
        </section>
      )}

      {/* ===== AGENDA / TIMELINE ===== */}
      {agendaItems.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 pb-8">
          <HeartDivider />
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-center mb-10"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Plan dnia
          </motion.h2>
          <div className="ml-4 md:ml-12">
            {agendaItems.map((item, idx) => (
              <TimelineItem
                key={idx}
                time={item.time}
                event={item.event}
                location={item.location}
                isLast={idx === agendaItems.length - 1}
                delay={idx * 0.1}
              />
            ))}
          </div>
        </section>
      )}

      {/* ===== RSVP FORM ===== */}
      <section className="max-w-2xl mx-auto px-6 pb-8" id="rsvp">
        <HeartDivider />
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-2xl md:text-3xl font-bold text-center mb-2"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Potwierdz obecnosc
        </motion.h2>
        <p className="text-center text-muted-foreground mb-8">
          Prosimy o potwierdzenie do dnia wesela
        </p>

        <AnimatePresence mode="wait">
          {rsvpSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Dziekujemy!
              </h3>
              <p className="text-muted-foreground">
                {rsvpForm.rsvp === 'confirmed'
                  ? 'Cieszymy sie, ze bedziesz z nami w tym wyjatkowym dniu!'
                  : 'Szkoda, ze nie mozesz byc z nami. Dziekujemy za odpowiedz.'}
              </p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => {
                  setRsvpSuccess(false);
                  setRsvpForm({
                    email: '',
                    name: '',
                    rsvp: 'confirmed',
                    dietary: '',
                    phone: '',
                    plus_one: false
                  });
                }}
              >
                Wyslij kolejne potwierdzenie
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleRsvpSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="rsvp-name" className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        Imie i nazwisko *
                      </Label>
                      <Input
                        id="rsvp-name"
                        value={rsvpForm.name}
                        onChange={(e) => updateRsvpField('name', e.target.value)}
                        placeholder="Anna Kowalska"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="rsvp-email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        Adres email *
                      </Label>
                      <Input
                        id="rsvp-email"
                        type="email"
                        value={rsvpForm.email}
                        onChange={(e) => updateRsvpField('email', e.target.value)}
                        placeholder="anna@example.com"
                        required
                      />
                    </div>

                    {/* RSVP choice */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-muted-foreground" />
                        Twoja odpowiedz *
                      </Label>
                      <RadioGroup
                        value={rsvpForm.rsvp}
                        onValueChange={(val) => updateRsvpField('rsvp', val)}
                        className="flex flex-col sm:flex-row gap-4"
                      >
                        <label
                          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all flex-1 ${
                            rsvpForm.rsvp === 'confirmed'
                              ? 'border-green-500 bg-green-50'
                              : 'border-border hover:border-green-300'
                          }`}
                        >
                          <RadioGroupItem value="confirmed" id="rsvp-confirmed" />
                          <div>
                            <p className="font-medium text-green-700">Potwierdzam</p>
                            <p className="text-xs text-muted-foreground">Bede na weselu</p>
                          </div>
                        </label>
                        <label
                          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all flex-1 ${
                            rsvpForm.rsvp === 'declined'
                              ? 'border-red-500 bg-red-50'
                              : 'border-border hover:border-red-300'
                          }`}
                        >
                          <RadioGroupItem value="declined" id="rsvp-declined" />
                          <div>
                            <p className="font-medium text-red-700">Nie moge byc</p>
                            <p className="text-xs text-muted-foreground">
                              Niestety nie dam rady
                            </p>
                          </div>
                        </label>
                      </RadioGroup>
                    </div>

                    <Separator />

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="rsvp-phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        Telefon
                      </Label>
                      <Input
                        id="rsvp-phone"
                        type="tel"
                        value={rsvpForm.phone}
                        onChange={(e) => updateRsvpField('phone', e.target.value)}
                        placeholder="+48 123 456 789"
                      />
                    </div>

                    {/* Dietary */}
                    <div className="space-y-2">
                      <Label htmlFor="rsvp-dietary" className="flex items-center gap-2">
                        <UtensilsCrossed className="w-4 h-4 text-muted-foreground" />
                        Preferencje dietetyczne
                      </Label>
                      <Textarea
                        id="rsvp-dietary"
                        value={rsvpForm.dietary}
                        onChange={(e) => updateRsvpField('dietary', e.target.value)}
                        placeholder="Np. wegetarianska, bezglutenowa, alergie pokarmowe..."
                        rows={2}
                        className="resize-none"
                      />
                    </div>

                    {/* Plus one */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                      <Checkbox
                        id="rsvp-plus-one"
                        checked={rsvpForm.plus_one}
                        onCheckedChange={(checked) =>
                          updateRsvpField('plus_one', Boolean(checked))
                        }
                        className="mt-0.5"
                      />
                      <div>
                        <Label
                          htmlFor="rsvp-plus-one"
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <UserPlus className="w-4 h-4 text-muted-foreground" />
                          Przyjde z osoba towarzyszaca
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Zaznacz, jesli chcesz przyjsc z partnerem / partnerkq
                        </p>
                      </div>
                    </div>

                    {/* Submit */}
                    <Button
                      type="submit"
                      className="w-full h-12 bg-rose-500 hover:bg-rose-600 text-white text-base"
                      disabled={rsvpSubmitting || !rsvpForm.name || !rsvpForm.email}
                    >
                      {rsvpSubmitting ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Send className="w-5 h-5 mr-2" />
                      )}
                      Wyslij potwierdzenie
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* ===== GIFT REGISTRY ===== */}
      {gift_registry_url && (
        <section className="max-w-2xl mx-auto px-6 pb-8 text-center">
          <HeartDivider />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Gift className="w-10 h-10 text-rose-400 mx-auto mb-4" />
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Lista prezentow
            </h2>
            <p className="text-muted-foreground mb-6">
              Waszym prezentem jest Wasza obecnosc, ale jesli chcielibyscie nas obdarowac:
            </p>
            <Button
              asChild
              size="lg"
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              <a href={gift_registry_url} target="_blank" rel="noopener noreferrer">
                <Gift className="w-4 h-4 mr-2" />
                Zobacz liste prezentow
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </motion.div>
        </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-6 border-t border-border mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-5 h-5 text-rose-300 mx-auto mb-3" />
          <p
            className="text-muted-foreground text-sm"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {couple_name && `${couple_name} — `}
            {wedding_date && formatWeddingDate(wedding_date)}
          </p>
          {photographer_name && (
            <p className="text-xs text-muted-foreground/60 mt-2 flex items-center justify-center gap-1.5">
              <Camera className="w-3 h-3" />
              Fotografia: {photographer_name}
            </p>
          )}
          {branding?.logo_url && (
            <img
              src={branding.logo_url}
              alt="Logo"
              className="h-8 mx-auto mt-4 opacity-60"
            />
          )}
        </div>
      </footer>
    </div>
  );
}
